/**
 * evaluateMenuRelevant
 * Evalúa la visibilidad de un elemento del menú basado en la estructura "relevant_transformed"
 * y los datos completos del formulario.
 *
 * Estructuras soportadas:
 * - Array vacío []: retorna true
 * - Array con elementos: se evalúa cada elemento y se combinan con AND por defecto
 * - Objeto de tipo "function" con name "selected": verifica membresía del segundo argumento
 *   dentro del valor del campo (soporta número, string, string CSV "a,b,c" y array)
 * - Objeto de tipo "comparison": comparadores =, ==, !=, <, >, <=, >= (manejo flexible de tipos)
 * - Objeto de tipo "logical": op "and" / "or" con items (arreglo de nodos)
 *
 * Notas de compatibilidad:
 * - Si se recibe un string como expresión (legacy), se retorna true por defecto.
 */

import { normalizeFieldToList, equalsFlexible, compareValues, getField } from './expressionValueHelpers';

function isArray(val) { return Array.isArray(val); }
function isObject(val) { return val !== null && typeof val === 'object' && !Array.isArray(val); }

// Bandera de debug controlada por ventana o variable de entorno
const DEBUG_EVALUATE = (typeof window !== 'undefined' && Boolean(window?.SIRIO_DEBUG_EVALUATE)) || (typeof process !== 'undefined' && process?.env?.REACT_APP_DEBUG_EVALUATE === 'true');

function evalArg(arg, datos) {
  if (!arg || !arg.type) return undefined;
  if (arg.type === 'literal') return arg.value;
  if (arg.type === 'field') return getField(datos, arg.name);
  // Extendible: otros tipos de argumentos en futuro
  return undefined;
}

function evalFunction(node, datos) {
  const name = (node?.name || '').toLowerCase();
  const args = Array.isArray(node?.args) ? node.args : [];
  const hasDirectField = typeof node?.field === 'string';
  const hasDirectValue = Object.prototype.hasOwnProperty.call(node || {}, 'value');

  if (name === 'selected') {
    // selected(field, value)
    let fieldVal;
    let target;

    if (hasDirectField) {
      fieldVal = getField(datos, node.field);
    } else {
      fieldVal = evalArg(args[0], datos);
    }

    if (hasDirectValue) {
      const v = node.value;
      target = (v && typeof v === 'object' && v.type) ? evalArg(v, datos) : v;
    } else {
      target = evalArg(args[1], datos);
    }

    const list = normalizeFieldToList(fieldVal);
    const targetStr = String(target ?? '').trim();
    const result = list.some(item => equalsFlexible(item, targetStr));
    if (DEBUG_EVALUATE) {
      const fieldName = hasDirectField ? node.field : (args[0]?.name || '(arg0)');
      console.log('[EVAL selected]', { field: fieldName, rawFieldVal: fieldVal, normalizedList: list, target: targetStr, result });
    }
    return result;
  }

  // Otras funciones podrían agregarse aquí (e.g., contains, in, not, etc.)
  return false;
}

function evalComparison(node, datos) {
  const fieldVal = getField(datos, node.field);
  const v = node.value;
  const rightVal = (v && typeof v === 'object' && v.type) ? evalArg(v, datos) : v;
  const op = node.operator || '=';
  const result = compareValues(op, fieldVal, rightVal);
  if (DEBUG_EVALUATE) {
    console.log('[EVAL comparison]', { field: node.field, operator: op, left: fieldVal, right: rightVal, result });
  }
  return result;
}

function evalLogical(node, datos) {
  const op = (node?.op || 'and').toLowerCase();
  const items = Array.isArray(node?.items) ? node.items : (Array.isArray(node?.expressions) ? node.expressions : []);
  if (op === 'and') return items.every(item => evalNode(item, datos));
  if (op === 'or') return items.some(item => evalNode(item, datos));
  // Default: AND
  return items.every(item => evalNode(item, datos));
}

function evalNode(node, datos) {
  if (!node || typeof node !== 'object') return true;
  const type = node.type;
  if (type === 'function') return evalFunction(node, datos);
  if (type === 'comparison') return evalComparison(node, datos);
  if (type === 'logical') return evalLogical(node, datos);
  // Tipos desconocidos: considerar true para no bloquear
  return true;
}

export default function evaluateMenuRelevant(datosFormulario, relevantTransformed) {
  try {
    // Mostrar grupos por defecto si no hay datos de respuestas disponibles
    if (!datosFormulario || Object.keys(datosFormulario).length === 0) return true;

    if (relevantTransformed === undefined || relevantTransformed === null) return true;


    // Array de condiciones
    if (Array.isArray(relevantTransformed)) {
      if (relevantTransformed.length === 0) return true; // CASO 2
      // Por defecto, combinar con AND todas las condiciones del arreglo
      return relevantTransformed.every(item => evalNode(item, datosFormulario || {}));
    }

    // Objeto único
    if (isObject(relevantTransformed)) {
      return evalNode(relevantTransformed, datosFormulario || {});
    }

    // Cualquier otro caso
    return true;
  } catch (e) {
    // En caso de error, considerar visible por seguridad
    // console.error('[evaluateMenuRelevant] Error al evaluar:', e);
    return true;
  }
}